<script lang="ts" setup>
import Link1 from '@/components/Button/Link1.vue'
import type { FormInstance } from 'element-plus/es/components/form'
import { useContactStore, useGlobalStore } from '@/stores'
import { useReCaptcha } from 'vue-recaptcha-v3'
import { useReCaptchaV3 } from '@/utils/helper'
import { ContactApi } from '~/utils/api'
import _ from 'lodash'

const useReCaptchaInstance = useReCaptcha()!
const contactStore = useContactStore()
const globalStore = useGlobalStore()
const formRef = ref<FormInstance>()
const form = contactStore.form

const submitBtnText: Ref<string> = ref('送出')
const submitBtnStatus: Ref<string> = ref('')

const handleSubmit = async () => {
  if (!formRef.value) return false
  // 避免double click
  if (submitBtnStatus.value === '') {
    return formRef.value.validate(async (valid, invalidFields) => {
      if (valid) {
        const token = await useReCaptchaV3(useReCaptchaInstance, 'contact')
        form.google_recaptcha_token = token || ''
        form.class_id = 1
        submitBtnText.value = '處理中...'
        submitBtnStatus.value = 'loading'
        const params = _.pick(form, [
          'class_id', 'name', 'email', 'phone', 'content', 'google_recaptcha_token', 'others'
        ]);

        (new ContactApi).submitContact(params).then((res) => {
          setTimeout(() => {
            contactStore.resetForm()
            submitBtnText.value = '完成'
            submitBtnStatus.value = 'done'
            formRef.value?.resetFields()
            globalStore.setDialogVisible({
              visible: true,
              title: '提示',
              message: '已成功送出。',
            })
          }, 500)
        }).catch((err) => {
          submitBtnText.value = '送出'
          submitBtnStatus.value = ''
          globalStore.setDialogVisible({
            visible: true,
            title: '提示',
            message: '請檢查填寫欄位是否正確。',
          })
        })
      }
    })
  }
}
</script>

<template>
  <section class="contact" style="background-image: url('./images/story/contact.jpg');">
    <div class="contact__inner">
      <div class="contact__left">
        <div class="contact__left__inner">
          <div class="contact__left__en">contact</div>
          <div class="contact__left__tw">聯絡我們</div>
          <div class="contact__left__text">恆隆行非常重視您的意見，若您有任何疑問，可先參考網頁上「常見問題」的解答，或者直接在此處留下問題，亦可於上班時間聯絡客服人員，我們將竭誠為您服務
          </div>
        </div>
      </div>
      <div class="contact__right">
        <el-form :model="form" class="contact__form" ref="formRef">
          <div class="contact__form__item">
            <el-form-item prop="name" :rules="[
          {
            required: true,
            message: '姓名為必填欄位'
          }
        ]">
              <el-input v-model="form.name" class="h-input h-input-require" placeholder="尊姓大名" />
            </el-form-item>
          </div>
          <div class="contact__form__item">
            <el-form-item prop="email" :rules="[
          {
            required: true,
            message: '電子信箱為必填欄位'
          },
          {
            type: 'email',
            message: '請輸入正確的電子信箱格式'
          }
        ]">
              <el-input v-model="form.email" class="h-input h-input-require" placeholder="電子信箱" />
            </el-form-item>
          </div>
          <div class="contact__form__item">
            <el-form-item prop="phone" :rules="[
          {
            required: true,
            message: '聯絡電話為必填欄位',
          },
        ]">
              <el-input v-model="form.phone" class="h-input" placeholder="聯絡電話" />
            </el-form-item>
          </div>
          <div class="contact__form__item">
            <el-form-item prop="others.company_name" :rules="[
          {
            required: true,
            message: '所屬公司為必填欄位'
          }
        ]">
              <el-input v-model="form.others.company_name" class="h-input h-input-require" placeholder="所屬公司" />
            </el-form-item>
          </div>
          <div class="contact__form__item">
            <el-form-item prop="content" :rules="[
          {
            required: true,
            message: '合作內容為必填欄位',
          },
        ]">
              <el-input v-model="form.content" class="h-textarea h-textarea-require" placeholder="合作內容"
                type="textarea" />
            </el-form-item>
          </div>
          <div class="contact__form__item">
            <Link1 color="gray-3" class="cursor-pointer" @click="handleSubmit">{{ submitBtnText }}</Link1>
          </div>
        </el-form>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.contact {
  padding-top: toRem(250);
  margin-left: toRem(-24);
  margin-right: toRem(-24);
  background-repeat: no-repeat;
  background-size: cover;

  @include max-media(767) {
    background-image: none;
  }

  &__inner {
    @include grid;
    align-items: center;
    // padding-left: toRem(24);
    // padding-right: toRem(24);
  }

  &__left {
    grid-column: 2 / span 4;
    padding: toRem(100) toRem(50) toRem(130);
    background-color: $color-white-2;

    @include max-media(1080) {
      grid-column: 1 / span 4;
      padding: toRem(60) toRem(30);
    }

    @include max-media(767) {
      grid-column: 1 / span 12;
      background-image: url('../../../public/images/story/contact.jpg');
      background-repeat: no-repeat;
      background-size: cover;
      margin-left: toRem(-24);
      margin-right: toRem(-24);
      padding: toRem(330) 0 0;
      @include grid(4, 24);
      padding-left: 0.24rem;
    }

    &__inner {
      @include max-media(767) {
        padding-left: toRem(24);
        padding-right: toRem(24);
        background-color: $color-white-2;
        grid-column: 1 / span 3;
        padding: toRem(60) toRem(30);
      }
    }

    &__en {
      margin-bottom: toRem(24);
      font-family: $font-TextaAlt;
      font-size: toRem(80);
      font-weight: 500;
      line-height: 0.8;
      color: $color-brand-3;

      @include max-media(1080) {
        font-size: toRem(60);
      }
    }

    &__tw {
      margin-bottom: toRem(24);
      font-size: toRem(28);
      font-weight: 500;
      line-height: 1.3;
    }

    &__text {
      font-size: toRem(16);
      line-height: 1.45;
    }
  }

  &__right {
    grid-column: 7 / span 6;
    padding: toRem(120) toRem(0) toRem(30);
    background-color: $color-white-2;

    @include max-media(1080) {
      padding-top: toRem(55);
      padding-bottom: toRem(55);
    }

    @include min-media(768) {
      @include grid(6, 24);
    }

    @include max-media(767) {
      grid-column: 1 / span 12;
      padding-bottom: toRem(0);
      padding-left: toRem(24);
      padding-right: toRem(24);
    }
  }

  &__form {
    grid-column: 2 / span 4;
  }
}
</style>
